/**
 * Games: schemas, catalog grouping, queries and mutations (GET/POST/PATCH/DELETE /api/games, plus
 * /games/:id/rtp-analytics and /games/:id/players-rtp).
 *
 * Traps handled here (rgs-api-contract):
 * - the list and the detail return the raw rows, EngineApiKey included: the schemas drop it, so it
 *   never reaches the query cache, and `loadGameEngineKey` fetches it for one reveal (#secrets);
 * - GET /games/:id returns JSON columns as strings and `Paytable` is null whenever `MetaData` is (#22);
 * - Category and Type must be the exact enum strings (#23); UUID is client-supplied on create;
 * - a write that hits a unique key answers 409 "Duplicate entry found"; on games that key is the UUID;
 * - RTP is stored as a 0–1 fraction, but a few rows hold percentages or nonsense: every value goes
 *   through `normalizeRtp` and bad ones are flagged, never guessed;
 * - rtp-analytics mixes units: `configuredRtp` is the stored fraction, `baseRtp`/`actualTotalRtp`
 *   are percentages, and amounts are EUR (legacy query; a missing exchange rate counts as 1:1).
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, isApiError } from '@/api/errors';
import { http } from '@/api/http';
import { encodePagedQuery, pagedSchema, type FilterDescriptor, type PagedQuery } from '@/api/paged-query';
import { decimal, flag, hexId, jsonColumn } from '@/api/zod-helpers';
import { formatRtp } from '@/lib/format';
import { normalizeRtp, type NormalizedRtp } from '@/lib/rtp';
import { filterParam, listSearchSchema, pageSizeStore, parseSortParam } from '@/lib/url-state';

/** `rgs-database` enums: the API rejects anything else. */
export const GAME_CATEGORIES = ['Slot', 'Originals', 'Arcade'] as const;
export const GAME_TYPES = ['Lines', 'Ways', 'Clusters', 'Scatters', 'Multiplayer', 'Singleplayer'] as const;

/** Column limits from the entity (varchar lengths). */
export const GAME_LIMITS = {
  Name: 45,
  EngineApiPath: 150,
  EngineApiKey: 32,
  RTPEngineVersion: 45,
  Thumbnail: 255,
  MarketingMaterialsZip: 150,
} as const;

// ---- schemas ------------------------------------------------------------------------------------

/** `{ default: "https://…" }`: platform or skin name → UI URL. */
const urlMap = jsonColumn(z.unknown()).transform((v): Record<string, string> => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  return Object.fromEntries(Object.entries(v).filter((e): e is [string, string] => typeof e[1] === 'string'));
});

const optionalText = z
  .string()
  .nullish()
  .transform((v) => v ?? '');

const gameFields = {
  Id: z.coerce.number().int(),
  UUID: hexId.nullish().transform((v) => v ?? null),
  Name: z.string(),
  EngineApiPath: optionalText,
  Status: flag,
  Configuration: jsonColumn(z.unknown()).optional(),
  GameUIUrl: urlMap.optional().transform((v) => v ?? {}),
  VolatilityIndex: decimal.nullish().transform((v) => v ?? null),
  /** Raw, for `normalizeRtp`: a DECIMAL string, sometimes a percentage or 0. */
  RTP: z.union([z.string(), z.number()]).nullish(),
  RTPEngineVersion: optionalText,
  ConfidenceIndex: decimal.nullish().transform((v) => v ?? null),
  Thumbnail: optionalText,
  RNGMonitoringEnabled: flag,
  RTPMonitoringEnabled: flag,
  Category: optionalText,
  Type: optionalText,
  MetaData: jsonColumn(z.unknown()).optional(),
  Description: optionalText,
  Priority: z.coerce
    .number()
    .nullish()
    .transform((v) => v ?? 0),
  MarketingMaterialsZip: optionalText,
  Paytable: jsonColumn(z.unknown()).optional(),
};

/** List and detail rows: no EngineApiKey, ever. */
export const gameRowSchema = z.object(gameFields);
export const gameDetailSchema = gameRowSchema;

export type GameRow = z.infer<typeof gameRowSchema>;
export type GameDetail = z.infer<typeof gameDetailSchema>;

// ---- derived facts ------------------------------------------------------------------------------

export interface VariantName {
  /** `Vortex` for `Vortex 96`. */
  title: string;
  /** The RTP label (`96`), or null for a title without variants. */
  variant: string | null;
  /** RTPEngineVersion is empty but the name ends in an RTP-like number (`Plinko 92`). */
  versionMissing: boolean;
}

/** Variants are separate games named `<Title> <RTPEngineVersion>` (rgs-domain). */
export function splitVariant(name: string, version: string | null | undefined): VariantName {
  const v = (version ?? '').trim();
  const n = name.trim();
  if (v && n.endsWith(` ${v}`)) return { title: n.slice(0, -(v.length + 1)).trim(), variant: v, versionMissing: false };
  if (v) return { title: n, variant: v, versionMissing: false };
  const match = /^(.*\S)\s+(\d{2})$/.exec(n);
  if (match?.[1] && match[2] && Number(match[2]) >= 80) {
    return { title: match[1], variant: match[2], versionMissing: true };
  }
  return { title: n, variant: null, versionMissing: false };
}

export type GameIssueKind = 'rtp' | 'naming' | 'enum';

export interface GameIssue {
  kind: GameIssueKind;
  message: string;
}

export function gameIssues(game: GameRow, rtp: NormalizedRtp, name: VariantName): GameIssue[] {
  const issues: GameIssue[] = [];
  const raw = game.RTP === null || game.RTP === undefined ? '' : String(game.RTP).trim();
  if (rtp.issue === 'missing')
    issues.push({ kind: 'rtp', message: `RTP isn't set${raw ? ` (stored as ${raw})` : ''}.` });
  if (rtp.issue === 'out-of-range') issues.push({ kind: 'rtp', message: `RTP ${raw} is outside 50–100%.` });
  if (rtp.issue === 'percent-like') {
    issues.push({ kind: 'rtp', message: `RTP is stored as a percentage (${raw}); the API expects a 0–1 fraction.` });
  }
  if (name.variant && rtp.fraction !== null && Math.abs(rtp.fraction * 100 - Number(name.variant)) >= 1) {
    issues.push({ kind: 'naming', message: `Named ${name.variant} but its RTP is ${formatRtp(rtp.fraction)}.` });
  }
  if (name.versionMissing) {
    issues.push({ kind: 'naming', message: "RTP engine version is empty; it's grouped by its name." });
  }
  if (game.Category && !(GAME_CATEGORIES as readonly string[]).includes(game.Category)) {
    issues.push({ kind: 'enum', message: `Unknown category "${game.Category}".` });
  }
  if (game.Type && !(GAME_TYPES as readonly string[]).includes(game.Type)) {
    issues.push({ kind: 'enum', message: `Unknown type "${game.Type}".` });
  }
  return issues;
}

export interface GameView<T extends GameRow = GameRow> extends VariantName {
  game: T;
  rtp: NormalizedRtp;
  issues: GameIssue[];
}

export function toGameView<T extends GameRow>(game: T): GameView<T> {
  const rtp = normalizeRtp(game.RTP);
  const name = splitVariant(game.Name, game.RTPEngineVersion);
  return { ...name, game, rtp, issues: gameIssues(game, rtp, name) };
}

/** A usable thumbnail URL, or null (`N/A`, empty and relative junk don't count). */
export function thumbnailUrl(thumbnail: string): string | null {
  return /^https:\/\//.test(thumbnail.trim()) ? thumbnail.trim() : null;
}

export interface GameTitle {
  key: string;
  title: string;
  /** Sorted by RTP label, lowest first. */
  variants: GameView[];
  categories: string[];
  types: string[];
  activeCount: number;
  issueCount: number;
  thumbnail: string | null;
}

const uniq = (values: string[]) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));

export function groupByTitle(games: GameRow[]): GameTitle[] {
  const byTitle = new Map<string, GameView[]>();
  for (const view of games.map(toGameView)) {
    const key = view.title.toLowerCase();
    const list = byTitle.get(key);
    if (list) list.push(view);
    else byTitle.set(key, [view]);
  }
  return [...byTitle.entries()].map(([key, views]) => {
    const variants = [...views].sort(
      (a, b) => Number(a.variant ?? 0) - Number(b.variant ?? 0) || a.game.Name.localeCompare(b.game.Name),
    );
    return {
      key,
      title: variants[0]?.title ?? key,
      variants,
      categories: uniq(variants.map((v) => v.game.Category)),
      types: uniq(variants.map((v) => v.game.Type)),
      activeCount: variants.filter((v) => v.game.Status).length,
      issueCount: variants.reduce((n, v) => n + v.issues.length, 0),
      thumbnail: variants.map((v) => thumbnailUrl(v.game.Thumbnail)).find(Boolean) ?? null,
    };
  });
}

// ---- catalog (list) -----------------------------------------------------------------------------

/**
 * The catalog is grouped by title, which the server can't do (the games allow-list is Id, UUID,
 * Name and Status). It's small (86 games on dev), so the page loads every game matching the server
 * filters in one request and groups, filters (category, type, issues), sorts and pages the titles
 * client-side. If the catalog outgrows CATALOG_LIMIT the page says so.
 */
export const CATALOG_LIMIT = 1000;

export const GAME_SORT_FIELDS = ['title', 'variants', 'issues'] as const;
export type GameSortField = (typeof GAME_SORT_FIELDS)[number];

export const gamesSearchSchema = listSearchSchema({
  sortFields: GAME_SORT_FIELDS,
  filters: {
    category: filterParam.enum(GAME_CATEGORIES),
    type: filterParam.enum(GAME_TYPES),
    active: filterParam.boolean(),
    issues: filterParam.boolean(),
  },
});

export type GamesSearch = z.infer<typeof gamesSearchSchema>;

/**
 * The server ANDs its filters, so one search can't try both Name and UUID. Eight hex characters or
 * more mean a UUID (whole or part): no game name looks like that, while short words like "Facade" do.
 */
const UUID_SEARCH_MIN_LENGTH = 8;
const uuidHex = (q: string) => q.replace(/-/g, '');
const isUuidSearch = (q: string) => uuidHex(q).length >= UUID_SEARCH_MIN_LENGTH && /^[0-9a-f-]+$/i.test(q);
/** Uppercase because the server matches on HEX(uuid), whatever the column's collation. */
const uuidFilter = (q: string): FilterDescriptor => ({
  field: 'UUID',
  operator: 'contains',
  value: uuidHex(q).toUpperCase(),
});

/** Only what the server filters on goes into the request (and the query key). */
export function catalogServerQuery(search: Pick<GamesSearch, 'q' | 'active'>): PagedQuery {
  const filters: FilterDescriptor[] = [];
  const q = search.q?.trim();
  if (q) filters.push(isUuidSearch(q) ? uuidFilter(q) : { field: 'Name', operator: 'contains', value: q });
  if (search.active !== undefined) filters.push({ field: 'Status', operator: 'eq', value: search.active ? 1 : 0 });
  return { skip: 0, take: CATALOG_LIMIT, sort: { field: 'Name', dir: 'asc' }, filters };
}

export interface CatalogPage {
  titles: GameTitle[];
  /** Titles after filtering, before paging. */
  totalTitles: number;
  /** Every title the server rows make up, before the client-side filters. */
  titleCount: number;
  totalGames: number;
  truncated: boolean;
}

export function catalogPage(rows: GameRow[], serverTotal: number, search: GamesSearch): CatalogPage {
  const grouped = groupByTitle(rows);
  let titles = grouped;
  if (search.category) titles = titles.filter((t) => t.categories.includes(search.category ?? ''));
  if (search.type) titles = titles.filter((t) => t.types.includes(search.type ?? ''));
  if (search.issues !== undefined) titles = titles.filter((t) => t.issueCount > 0 === search.issues);
  const sort = parseSortParam(search.sort) ?? { field: 'title', dir: 'asc' };
  const dir = sort.dir === 'desc' ? -1 : 1;
  const value = (t: GameTitle): number | string =>
    sort.field === 'variants' ? t.variants.length : sort.field === 'issues' ? t.issueCount : t.title.toLowerCase();
  titles.sort((a, b) => {
    const x = value(a);
    const y = value(b);
    return (x < y ? -1 : x > y ? 1 : 0) * dir || a.title.localeCompare(b.title);
  });
  const size = search.size ?? pageSizeStore.get();
  const start = ((search.page ?? 1) - 1) * size;
  return {
    titles: titles.slice(start, start + size),
    totalTitles: titles.length,
    titleCount: grouped.length,
    totalGames: rows.length,
    truncated: serverTotal > rows.length,
  };
}

const catalogSchema = pagedSchema(gameRowSchema);

export const gamesKeys = {
  all: ['games'] as const,
  catalog: (query: PagedQuery) => ['games', 'catalog', query] as const,
  detail: (id: number) => ['games', 'detail', id] as const,
  rtp: (id: number, from: string, to: string) => ['games', 'rtp-analytics', id, from, to] as const,
  players: (id: number, params: Record<string, unknown>) => ['games', 'players-rtp', id, params] as const,
};

export const gamesCatalogQuery = (search: Pick<GamesSearch, 'q' | 'active'>) => {
  const query = catalogServerQuery(search);
  return queryOptions({
    queryKey: gamesKeys.catalog(query),
    queryFn: ({ signal }) => http('/games', { query: encodePagedQuery(query), signal }, catalogSchema),
    placeholderData: keepPreviousData,
  });
};

export const gameQuery = (id: number) =>
  queryOptions({
    queryKey: gamesKeys.detail(id),
    queryFn: ({ signal }) => http(`/games/${id}`, { signal }, gameDetailSchema),
  });

/** The engine key, fetched fresh for one reveal (or for a variant that reuses it) and never cached. */
export async function loadGameEngineKey(id: number): Promise<string> {
  const { EngineApiKey } = await http(`/games/${id}`, {}, z.object({ EngineApiKey: z.string().nullish() }));
  return EngineApiKey ?? '';
}

// ---- analytics ----------------------------------------------------------------------------------

/** The analytics tabs default to the last 30 UTC days, today included. */
export function defaultRange(now = new Date()): { from: string; to: string } {
  const day = (offset: number) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + offset));
    return d.toISOString().slice(0, 10);
  };
  return { from: day(-29), to: day(0) };
}

const rtpAnalyticsSchema = z.object({
  overall: z.object({
    totalRounds: z.coerce.number(),
    totalBet: z.coerce.number(),
    totalBaseWin: z.coerce.number(),
    totalBonusWin: z.coerce.number(),
    configuredRtp: z.union([z.string(), z.number()]).nullish(),
    /** Percentages (×100), unlike `configuredRtp`. */
    baseRtp: z.coerce.number(),
    bonusRtp: z.coerce.number(),
    actualTotalRtp: z.coerce.number(),
  }),
  bonusGames: z
    .array(
      z.object({
        name: z.string(),
        triggers: z.coerce.number(),
        totalWin: z.coerce.number(),
        /** Percentage of total bets. */
        featureRtp: z.coerce.number(),
      }),
    )
    .default([]),
});

export type GameRtpAnalytics = z.infer<typeof rtpAnalyticsSchema>;

export const gameRtpAnalyticsQuery = (id: number, from: string, to: string) =>
  queryOptions({
    queryKey: gamesKeys.rtp(id, from, to),
    queryFn: ({ signal }) =>
      http(`/games/${id}/rtp-analytics`, { query: { startDate: from, endDate: to }, signal }, rtpAnalyticsSchema),
    placeholderData: keepPreviousData,
  });

const playerRtpSchema = z.object({
  playerId: z.coerce.number(),
  totalRounds: z.coerce.number(),
  /** EUR. */
  totalDebit: z.coerce.number(),
  totalCredit: z.coerce.number(),
  ggr: z.coerce.number(),
  /** Percentage. */
  rtp: z.coerce.number(),
});

export type PlayerRtpRow = z.infer<typeof playerRtpSchema>;

export const PLAYER_RTP_SORT_FIELDS = ['playerId', 'totalRounds', 'totalDebit', 'totalCredit', 'ggr', 'rtp'] as const;

export const gamePlayersRtpQuery = (id: number, from: string, to: string, query: PagedQuery) =>
  queryOptions({
    queryKey: gamesKeys.players(id, { from, to, ...query }),
    queryFn: ({ signal }) =>
      http(
        `/games/${id}/players-rtp`,
        { query: { ...encodePagedQuery(query), startDate: from, endDate: to }, signal },
        pagedSchema(playerRtpSchema),
      ),
    placeholderData: keepPreviousData,
  });

// ---- writes -------------------------------------------------------------------------------------

/** `game_uuid` is the only unique key on games besides the id, so a 409 means the UUID is taken. */
const UUID_TAKEN = 'Another game already uses this UUID';

function nameUuidConflict(error: unknown): never {
  if (isApiError(error) && error.code === 'conflict') {
    throw new ApiError({ status: error.status, code: error.code, messages: [UUID_TAKEN], raw: error.raw });
  }
  throw error;
}

export function useUpdateGame(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      http(`/games/${id}`, { method: 'PATCH', body }, gameDetailSchema).catch(nameUuidConflict),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: gamesKeys.all });
    },
  });
}

export function useCreateGame() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      http('/games', { method: 'POST', body }, z.looseObject({ Id: z.coerce.number().int() })).catch(nameUuidConflict),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: gamesKeys.all });
    },
  });
}

export function useDeleteGame(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http(`/games/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: gamesKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: gamesKeys.all });
    },
  });
}

/** A new game's UUID: the API expects the client to supply 32 hex characters. */
export const newGameUuid = () => crypto.randomUUID().replace(/-/g, '');
